import {collection, doc, getDoc, getDocs} from 'firebase/firestore';
import {db} from '../../../shared/services/firebase';

const FIREBASE_TO_DOMAIN_CODE = {
  'permission-denied': 'STATS_PERMISSION_DENIED',
  'not-found': 'STATS_NOT_FOUND',
  unauthenticated: 'STATS_UNAUTHENTICATED',
};

const DOMAIN_ERROR_MESSAGES = {
  STATS_PERMISSION_DENIED: 'You do not have permission to view statistics.',
  STATS_NOT_FOUND: 'Configuration or edge data not found.',
  STATS_UNAUTHENTICATED: 'Please sign in again and retry.',
  STATS_CONFIG_LOAD_FAILED: 'Failed to load configuration.',
  STATS_TEMPLATE_LOAD_FAILED: 'Failed to load Modbus templates.',
};

const getFirebaseCode = (error) => {
  const code = error?.code;
  if (!code || typeof code !== 'string') return null;
  if (code.includes('/')) return code.split('/')[1];
  if (code.includes(':')) return code.split(':')[1];
  return code;
};

const toStatsError = (error, fallbackCode = 'STATS_CONFIG_LOAD_FAILED') => {
  const firebaseCode = getFirebaseCode(error);
  const code = FIREBASE_TO_DOMAIN_CODE[firebaseCode] || fallbackCode;
  const message = DOMAIN_ERROR_MESSAGES[code] || error?.message || 'Unknown error';
  return {
    code,
    message,
    rawCode: error?.code || null,
    firebaseCode,
    cause: error || null,
  };
};

const ok = (data = null) => ({ok: true, data, error: null});
const fail = (error) => ({ok: false, data: null, error});

const logStatsError = (context, errorInfo) => {
  console.error(`[statisticsApi:${context}] ${errorInfo.code}: ${errorInfo.message}`, errorInfo.cause || errorInfo);
};

/**
 * Get edge configuration and associated Modbus templates
 * Supports both modern (activeConfig) and legacy (chartsConfig) configurations
 */
export const getEdgeConfigAndTemplates = async (edgeId) => {
  try {
    const edgeRef = doc(db, "edges", edgeId);
    const edgeSnap = await getDoc(edgeRef);

    if (!edgeSnap.exists()) {
      const errorInfo = toStatsError(null, 'STATS_NOT_FOUND');
      logStatsError('getEdgeConfigAndTemplates', errorInfo);
      return fail(errorInfo);
    }

    const edgeData = edgeSnap.data();

    // Check for legacy chartsConfig
    if (edgeData.lifecycle?.status === "activated-legacy") {
      const chartsConfigId = edgeData.chartsConfig || 'default';
      const legacyConfigRef = doc(db, "chartsConfig", chartsConfigId);
      const legacyConfigSnap = await getDoc(legacyConfigRef);

      if (legacyConfigSnap.exists()) {
        return ok({
          config: legacyConfigSnap.data(),
          templates: {},
          isLegacy: true,
        });
      }

      const errorInfo = toStatsError(null, 'STATS_NOT_FOUND');
      logStatsError('getEdgeConfigAndTemplates', errorInfo);
      return fail(errorInfo);
    }

    // Modern config path
    const activeConfigRef = doc(db, "edges", edgeId, "activeConfig", "v1");
    const activeConfigSnap = await getDoc(activeConfigRef);

    if (!activeConfigSnap.exists()) {
      const errorInfo = toStatsError(null, 'STATS_NOT_FOUND');
      logStatsError('getEdgeConfigAndTemplates', errorInfo);
      return fail(errorInfo);
    }

    const config = JSON.parse(activeConfigSnap.data().value);

    // Load Modbus templates
    const templatesRef = collection(db, "modbusTemplates");
    const templatesSnap = await getDocs(templatesRef);
    const templates = {};

    templatesSnap.forEach((doc) => {
      templates[doc.id] = doc.data();
    });

    // Fallback to config JSON if templates collection is empty
    if (Object.keys(templates).length === 0 && config.modbusTemplates) {
      Object.assign(templates, config.modbusTemplates);
    }

    return ok({
      config,
      templates,
      isLegacy: false,
    });
  } catch (error) {
    const errorInfo = toStatsError(error, 'STATS_CONFIG_LOAD_FAILED');
    logStatsError('getEdgeConfigAndTemplates', errorInfo);
    return fail(errorInfo);
  }
};

